import { Link, useNavigate } from 'react-router-dom';
import { Folder } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import type { Collection } from '@maker-toolchest/shared';
import { useUIStore } from '@/stores/ui';
import { CollectionActionsMenu } from './CollectionActionsMenu';

// Get icon component from icon name string
function getIconComponent(iconName?: string | null): React.ComponentType<any> {
  if (!iconName) return Folder;

  // Convert kebab-case to PascalCase (e.g., "folder-open" -> "FolderOpen")
  const pascalCase = iconName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

  const LucideIcon = (LucideIcons as any)[pascalCase];
  if (LucideIcon) {
    return LucideIcon;
  }

  return Folder;
}

interface CollectionTableProps {
  collections: Collection[];
  parentNameMap: Map<string, string>;
  onDelete: (collection: Collection) => void;
  deletingId: string | null;
}

export function CollectionTable({
  collections,
  parentNameMap,
  onDelete,
  deletingId,
}: CollectionTableProps) {
  const navigate = useNavigate();
  const { setCollectionsFilterOpen, openCollectionSidebar } = useUIStore();

  const handleViewModels = (collectionId: string) => {
    setCollectionsFilterOpen(true);
    navigate(`/models?collection=${collectionId}`);
  };

  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="w-full">
        <thead className="bg-background-subtle border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Name
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Parent
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Items
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Depth
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-foreground-muted uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-background divide-y divide-border">
          {collections.map((collection) => {
            const IconComponent = getIconComponent(collection.icon);
            const isDeleting = deletingId === collection.id;

            return (
              <tr
                key={collection.id}
                className="hover:bg-background-subtle transition-colors"
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <IconComponent
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: collection.color || '#6b7280' }}
                    />
                    <div className="min-w-0">
                      <Link
                        to={`/collections/${collection.id}`}
                        className="font-medium text-foreground hover:text-primary"
                      >
                        {collection.name}
                      </Link>
                      {collection.description && (
                        <p className="mt-1 text-sm text-foreground-muted line-clamp-1">
                          {collection.description}
                        </p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground-muted">
                    {collection.parentId
                      ? parentNameMap.get(collection.id) || 'Sub-collection'
                      : 'Root'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">
                    {collection.modelCount || 0}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground-muted">
                    {collection.depth}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <CollectionActionsMenu
                      collection={collection}
                      onViewModels={() => handleViewModels(collection.id)}
                      onAddSubCollection={() => openCollectionSidebar({
                        defaultParentId: collection.id,
                        parentCollectionName: collection.name,
                      })}
                      onEdit={() => openCollectionSidebar({ collection })}
                      onDelete={() => onDelete(collection)}
                      isDeleting={isDeleting}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
